import {
  ApiError,
  authApi,
  clearAccessToken,
  getApiErrorMessage,
  restoreSession,
  setAccessToken,
  setUnauthorizedHandler,
} from '@milliswift/api-client'
import { APP_URLS } from '@milliswift/api-client'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { LoginResponse, User } from '@milliswift/types'

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

export interface LoginResult {
  /** True when the server returned an MFA challenge instead of a session. */
  mfaRequired: boolean
  mfaToken?: string
}

export interface AuthContextValue {
  status: AuthStatus
  user: User | null
  isAuthenticated: boolean
  /** Credentials accepted but the session is not established yet. */
  pendingMfaToken: string | null

  login: (email: string, password: string) => Promise<LoginResult>
  /** Complete a login that returned an MFA challenge. */
  verifyMfa: (code: string) => Promise<void>
  /** Complete MFA enrolment on an already-authenticated session. */
  confirmMfaEnrollment: (code: string) => Promise<string[]>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export interface AuthProviderProps {
  children: React.ReactNode
  /**
   * Where to send the browser when the session ends. Defaults to the accounts
   * app's sign-in page with a return path back to the current app.
   */
  signInUrl?: string
  /** Skip the sign-in redirect (the accounts app manages its own routing). */
  redirectOnUnauthorized?: boolean
}

/**
 * Session state for a milliswift app.
 *
 * The access token is held in memory by the api-client, never in storage. On
 * mount we ask the API to refresh from the HttpOnly cookie, which is what
 * makes a reload (or a hop between apps) keep you signed in.
 */
export function AuthProvider({
  children,
  signInUrl,
  redirectOnUnauthorized = true,
}: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<User | null>(null)
  const [pendingMfaToken, setPendingMfaToken] = useState<string | null>(null)

  const signInHref = useMemo(() => {
    if (signInUrl) return signInUrl
    const returnTo = encodeURIComponent(window.location.href)
    return `${APP_URLS.accounts}/login?return_to=${returnTo}`
  }, [signInUrl])

  const clearSession = useCallback(
    (redirect = false) => {
      clearAccessToken()
      setUser(null)
      setPendingMfaToken(null)
      setStatus('anonymous')
      if (redirect && redirectOnUnauthorized) {
        window.location.href = signInHref
      }
    },
    [redirectOnUnauthorized, signInHref]
  )

  // A rejected refresh means the session is genuinely over.
  useEffect(() => {
    setUnauthorizedHandler(() => clearSession(true))
    return () => setUnauthorizedHandler(null)
  }, [clearSession])

  // Restore on mount.
  useEffect(() => {
    let cancelled = false

    const bootstrap = async () => {
      const token = await restoreSession()
      if (cancelled) return

      if (!token) {
        setStatus('anonymous')
        return
      }

      try {
        const me = await authApi.me()
        if (cancelled) return
        setUser(me)
        setStatus('authenticated')
      } catch {
        if (cancelled) return
        clearSession()
      }
    }

    void bootstrap()
    return () => {
      cancelled = true
    }
  }, [clearSession])

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const response: LoginResponse = await authApi.login({ email, password })

      if (response.mfa_required) {
        if (!response.mfa_token) {
          throw new ApiError({
            message: 'The server asked for a verification code but sent no challenge token.',
            status: 500,
            code: 'malformed_response',
          })
        }
        setPendingMfaToken(response.mfa_token)
        return { mfaRequired: true, mfaToken: response.mfa_token }
      }

      if (!response.access_token) {
        throw new ApiError({
          message: 'Sign-in succeeded but no access token was returned.',
          status: 500,
          code: 'malformed_response',
        })
      }

      setAccessToken(response.access_token)

      // The login response may omit the user; fetch it rather than guessing.
      const me = response.user ?? (await authApi.me())
      setUser(me)
      setStatus('authenticated')
      setPendingMfaToken(null)

      return { mfaRequired: false }
    },
    []
  )

  const verifyMfa = useCallback(
    async (code: string) => {
      if (!pendingMfaToken) {
        throw new ApiError({
          message: 'No sign-in is awaiting a verification code.',
          status: 400,
          code: 'no_pending_mfa',
        })
      }

      const response = await authApi.verifyMFA({ mfa_token: pendingMfaToken, code })

      if (!response.access_token) {
        throw new ApiError({
          message: 'Verification succeeded but no access token was returned.',
          status: 500,
          code: 'malformed_response',
        })
      }

      setAccessToken(response.access_token)
      const me = response.user ?? (await authApi.me())
      setUser(me)
      setStatus('authenticated')
      setPendingMfaToken(null)
    },
    [pendingMfaToken]
  )

  const confirmMfaEnrollment = useCallback(async (code: string) => {
    // Enrolment verifies against the current access token, not a login challenge.
    const response = await authApi.verifyMFA({ mfa_token: '', code })
    setUser((current) => (current ? { ...current, mfa_enabled: true } : current))
    return response.backup_codes ?? []
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // A failed logout should still clear local state — otherwise a user on a
      // flaky connection appears stuck signed in.
    } finally {
      clearAccessToken()
      setUser(null)
      setPendingMfaToken(null)
      setStatus('anonymous')
    }
  }, [])

  const refreshUser = useCallback(async () => {
    const me = await authApi.me()
    setUser(me)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      isAuthenticated: status === 'authenticated',
      pendingMfaToken,
      login,
      verifyMfa,
      confirmMfaEnrollment,
      logout,
      refreshUser,
    }),
    [status, user, pendingMfaToken, login, verifyMfa, confirmMfaEnrollment, logout, refreshUser]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside an <AuthProvider>')
  return context
}

/** Convenience for error toasts: maps an unknown error to readable copy. */
export { getApiErrorMessage }
